import { computed } from 'vue';
import {
  integer, maxLength, minLength, requiredIf,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { TypeCampaignEnum } from '@/types';
import { includesValuesValidator, requiredWithMessage, linkedBoxRequiredValidator } from '@/helpers/CustomValidator';
import type { FlowOperatorMetadata, ValidationResult, Period, Dependencies } from '@/types/flow-types';
import Form from './Form.vue';

export interface OpenEmailData {
  custom_title: string;
  custom_description: string;
  outputs_number: number;
  grant_additional_delay: string;
  extra_time: Period;
  type: 'open' | 'noopen';
  linked_box: string;
  funnel_template_ids: string;
  initial_number_outputs?: number | null;
}

export const createDefaultData = (overrides: Partial<OpenEmailData> = {}): OpenEmailData => ({
  custom_title: '',
  custom_description: '',
  outputs_number: 1,
  grant_additional_delay: '1',
  extra_time: {
    value: 1,
    unit: 'DAY',
  },
  type: 'open',
  linked_box: '[]',
  funnel_template_ids: '[]',
  ...overrides,
});

// Operators that can be linked to this filter
export const LINKABLE_OPERATOR_TYPES = ['boxsendemail', 'boxsendfbmessenger'];

const OpenEmail: FlowOperatorMetadata<OpenEmailData> = {
  id: 'openemail',
  kind: 'filter',
  label: 'automatedScenarios.filters.openEmail',
  icon: 'fa-envelope',
  hasConfiguration: true,
  availableIn: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],

  // This filter only appears if there's a sendemail or sendfbmessenger in the ancestors
  dependencies: {
    [TypeCampaignEnum.AUTOMATION]: {
      operators: {
        any_parents: LINKABLE_OPERATOR_TYPES,
      },
    },
    [TypeCampaignEnum.BULK]: {
      operators: {
        any_parents: LINKABLE_OPERATOR_TYPES,
      },
    },
  } as Dependencies,

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: ['boxabandonpanier', 'boxperiodiclaunching', 'boxvisitpage'],
    },
    [TypeCampaignEnum.BULK]: {
      nextOperators: ['boxchoosebulklist', 'boxaudiencesetup'],
    },
  },

  Form,

  defaultData: createDefaultData,

  outputs(data: OpenEmailData): number {
    return data.outputs_number ?? 1;
  },

  getDisplayLabel(data: OpenEmailData): string | null {
    return data.custom_title || null;
  },

  getSubtitle(data: OpenEmailData): string | null {
    return data.custom_description || null;
  },

  async validate(data: OpenEmailData): Promise<ValidationResult> {
    const normalizedData = {
      ...data,
      outputs_number: Number(data.outputs_number),
    };

    const rules = computed(() => ({
      custom_title: {
        requiredWithMessage: requiredWithMessage(),
        minLength: minLength(3),
        maxLength: maxLength(45),
      },
      outputs_number: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator([1, 2]),
      },
      grant_additional_delay: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator(['0', '1']),
      },
      extra_time: {
        value: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          integer,
        },
        unit: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          includesValuesValidator: includesValuesValidator(['SECOND', 'MINUTE', 'HOUR', 'DAY']),
        },
      },
      type: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator(['open', 'noopen']),
      },
      linked_box: {
        linkedBoxRequiredValidator: linkedBoxRequiredValidator(),
      },
    }));

    const v$ = useVuelidate(rules, normalizedData);
    const success = await v$.value.$validate();
    return {
      success,
      validate: v$,
    };
  },

  beforeInsert(data: OpenEmailData): OpenEmailData {
    // Remove initial_number_outputs if present (used for tracking output changes)
    const cleanData = { ...data };
    if ('initial_number_outputs' in cleanData) {
      delete (cleanData as any).initial_number_outputs;
    }
    return cleanData;
  },
};

export default OpenEmail;
